/** `lender` is the lending partner (Peso a Peso); it only ever sees the partner portal. */
export type UserRole = "customer" | "agent" | "admin" | "lender";

export interface User {
  id: string;
  fullName: string;
  email: string;
  role: UserRole;
  kycStatus: "pending" | "verified" | "rejected";
  emailVerified: boolean;
  /** False for an account created through Google or Apple until it sets one. */
  hasPassword: boolean;
  /** CLABE of the DEFAULT SPEI account (see SpeiAccount); null until one is registered. */
  speiClabe: string | null;
  /** Contact telephone; null until the customer provides one. */
  phone: string | null;
  /** True once the welcome tour (the five-screen onboarding pop-up) was finished or skipped. */
  welcomeSeen: boolean;
}

/**
 * Where a CLABE stands with Kuspit as the contract's withdrawal destination. Kuspit holds exactly
 * one per contract; withdrawals only go to the `pending`/`authorized` one. `pending` = sent with
 * its cover page, awaiting Kuspit's manual authorisation (minutes, up to 72 hours, never notified).
 */
export type KuspitDestinationStatus = "not_submitted" | "pending" | "authorized" | "rejected" | "replaced";

/** One of the customer's SPEI accounts. Exactly one is the default. */
export interface SpeiAccount {
  id: string;
  clabe: string;
  label: string | null;
  isDefault: boolean;
  createdAt: string;
  kuspit: {
    status: KuspitDestinationStatus;
    submittedAt: string | null;
    authorizedAt: string | null;
    message: string | null;
  };
}

/** The account withdrawals leave to, if the customer has registered one with Kuspit. */
export function registeredSpeiAccount(accounts: SpeiAccount[]): SpeiAccount | null {
  return accounts.find((account) => account.kuspit.status === "pending" || account.kuspit.status === "authorized") ?? null;
}

export interface SessionSummary {
  id: string;
  current: boolean;
  createdAt: string;
  expiresAt: string;
  lastRotatedAt: string | null;
}

export interface LinkedIdentity {
  provider: "google" | "apple";
  email: string | null;
  linkedAt: string;
  lastLoginAt: string | null;
}

export type OAuthProviderId = "google" | "apple";

export interface OAuthProvider {
  id: OAuthProviderId;
  label: string;
}

export type TransactionStatus = "pending" | "processing" | "settled" | "failed";
export type TransactionType = "deposit" | "withdrawal" | "interest" | "fee" | "adjustment";

export interface WalletTransaction {
  id: string;
  type: TransactionType;
  amount: number;
  signedAmount: number;
  currency: "MXN";
  status: TransactionStatus;
  description: string;
  /** A withdrawal waiting for its fund sale to settle before the SPEI transfer goes out. */
  awaitingLiquidation?: boolean;
  providerTransactionId: string | null;
  /** Clave de rastreo of a SPEI movement — the reference the customer can quote to their bank. */
  trackingKey: string | null;
  correlationId: string;
  createdAt: string;
  settledAt: string | null;
}

export interface Wallet {
  id: string;
  product: "libre" | "travel";
  currency: "MXN";
  status: "pending" | "active" | "suspended" | "closed";
  providerAccountRef: string | null;
  /**
   * The balance Kuspit reports — the balance of record, since money enters and leaves through
   * Kuspit. `source` is "unavailable" until Kuspit has answered once; the figures then read zero.
   */
  balance: {
    available: number;
    /** Earmarked by the customer's Apartados: part of `available` that cannot be withdrawn. */
    reserved: number;
    /** What the customer may withdraw or move into an Apartado right now. */
    free: number;
    /** The fund sub-ledger's split of Kuspit's figure (absent from older API builds). */
    pots?: FundPots;
    locked: number;
    accruedInterest: number;
    /** Instructions dispatched and not yet confirmed by Kuspit; informational, not part of `available`. */
    pendingIn: number;
    pendingOut: number;
    source: "kuspit" | "unavailable";
    asOf: string | null;
  };
  goal: WalletGoal | null;
  apartados: Array<{ id: string; tripName: string; status: ApartadoStatus; balance: number; goalAmount: number; requestId: string }>;
  verification: { kuspitVerifiedAt: string | null; required: boolean; withdrawalEligible: boolean; reason: "wallet_not_active" | "kuspit_verification_pending" | null };
  /** Kuspit's 3,000 UDI monthly cap on deposits while the file (expediente) is incomplete. */
  depositLimit: DepositLimit;
  investment: { enabled: boolean; fundLabel: string; infoText: string; openOrders: InvestmentOrder[]; recentOrders: InvestmentOrder[] };
  synchronization: { lastSyncedAt: string | null; lastAttemptAt: string | null; lastError: string | null; stale: boolean };
  transactions: WalletTransaction[];
  /** Kuspit's reported balance over time, one point per day, oldest first. Drives the growth chart. */
  balanceHistory: Array<{ at: string; available: number }>;
  createdAt: string;
}

export interface ApiEnvelope<T> { data: T }

/**
 * What the wallet may still receive this month. `applies` is false once Kuspit has the customer's
 * file complete (or enforcement is off); the figures are still sent so the client can explain them.
 */
export interface DepositLimit {
  applies: boolean;
  reason: "kuspit_file_incomplete" | null;
  limitUdi: number;
  udiValueMxn: number;
  limitMxn: number;
  usedMxn: number;
  remainingMxn: number;
  periodStart: string;
  periodEnd: string;
}

/** The customer's "sueño": what a Wallet Libre is saving towards. */
export interface WalletGoal {
  title: string | null;
  amount: number | null;
  /** ISO calendar date (YYYY-MM-DD). */
  date: string | null;
  photoId: string | null;
  photoUrl: string | null;
  /** 0–1 share of the target already saved; null without a target amount. */
  progress: number | null;
  remaining: number | null;
  updatedAt: string | null;
}

export type MediaKind = "gallery" | "region" | "destination" | "product" | "dream" | "general";

export interface MediaPhoto {
  id: string;
  kind: MediaKind;
  title: string | null;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  sortOrder: number;
  active: boolean;
  ownerId: string | null;
  url: string;
  createdAt: string;
}

export type DestinationCategory = "playa" | "ciudad" | "aventura" | "cultura";

export interface Destination {
  id: string;
  regionId: string;
  slug: string;
  name: string;
  country: string;
  subtitle: string | null;
  category: DestinationCategory;
  price: number | null;
  /** Prices are quoted per person in this currency. */
  currency: "MXN" | "USD";
  season: string | null;
  nights: number | null;
  /** Longer copy the destination card reveals behind "Más detalles". */
  description: string | null;
  perks: string[];
  photoId: string | null;
  photoUrl: string | null;
  sortOrder: number;
  active: boolean;
}

export interface Region {
  id: string;
  slug: string;
  name: string;
  subtitle: string | null;
  featured: string | null;
  flag: string | null;
  hint: string | null;
  photoId: string | null;
  photoUrl: string | null;
  sortOrder: number;
  active: boolean;
  destinationCount: number;
  destinations: Destination[];
}

export type ProductId = "libre" | "travel" | "tnpl";
export type BrandTint = "mediterraneo" | "caribe" | "maldivas" | "waikiki";

export interface Product {
  id: ProductId;
  eyebrow: string;
  title: string;
  tag: string;
  rateLabel: string;
  rateSuffix: string;
  badge: string | null;
  icon: string;
  tint: BrandTint | string;
  photoId: string | null;
  photoUrl: string | null;
  enabled: boolean;
  sortOrder: number;
}

export interface WalletLibreSettings {
  annualRatePercent: number;
  subtitle: string;
  legalNote: string;
  conditionsUrl: string;
  benefits: Array<{ icon: string; title: string; copy: string }>;
}

export interface AuthSettings {
  loginHeadline: string;
  loginPromise: string;
  signupHeadline: string;
  signupPromise: string;
  footer: string;
}

export interface TnplSettings {
  audience: "admins" | "all";
  annualRatePercent: number;
  minAmount: number;
  maxAmount: number;
  showPaymentEstimate: boolean;
  lenderName: string;
  authorizationText: string;
  privacyText: string;
  legalNotice: string;
}

export interface AppSettings {
  wallet_libre?: WalletLibreSettings;
  auth?: AuthSettings;
  tnpl?: TnplSettings;
  reservations?: ReservationSettings;
  investment?: InvestmentSettings;
}

// --- Travel Now · Pay Later --------------------------------------------------------------------
export type LoanStatus = "submitted" | "in_review" | "approved" | "rejected";
export type LoanTerm = 6 | 12;

/** GET /api/v1/loans/config — what this account may do and the copy/limits to show. */
export interface LoanConfig {
  enabled: boolean;
  audience: "admins" | "all";
  annualRatePercent: number;
  minAmount: number;
  maxAmount: number;
  termOptions: LoanTerm[];
  showPaymentEstimate: boolean;
  documentMaxBytes: number;
  lenderName: string;
  authorizationText: string;
  privacyText: string;
  legalNotice: string;
}

export interface LoanDocument {
  id: string;
  kind: "constancia_fiscal";
  mime: string;
  bytes: number;
  originalName: string | null;
  sha256: string;
  createdAt: string;
}

export interface LoanContact {
  fullName: string;
  email: string;
  phone: string;
}

/** The applicant's own view of an application (CURP masked, no reviewer internals). */
export interface LoanApplication {
  id: string;
  folio: string;
  status: LoanStatus;
  amount: number;
  currency: string;
  termMonths: LoanTerm;
  curpMasked: string;
  contact: LoanContact;
  annualRatePercent: number | null;
  estimatedMonthlyPayment: number | null;
  document: { id: string; originalName: string | null; bytes: number } | null;
  authorizationAcceptedAt: string;
  submittedAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
}

/** One row of the partner's application list. */
export interface PartnerLoanSummary {
  id: string;
  folio: string;
  status: LoanStatus;
  amount: number;
  currency: string;
  termMonths: LoanTerm;
  curp: string;
  applicant: LoanContact;
  account: { fullName: string; email: string };
  documentName: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  decidedAt: string | null;
}

/** The partner's full view: everything submitted plus evidence and the review trail. */
export interface PartnerLoanApplication extends Omit<LoanApplication, "document"> {
  curp: string;
  document: LoanDocument | null;
  applicant: { id: string; fullName: string; email: string; phone: string | null; kycStatus: string; accountStatus: string; memberSince: string } | null;
  authorization: { text: string; textSha256: string; acceptedAt: string; ipRecorded: boolean; userAgentRecorded: boolean };
  reviewedAt: string | null;
  reviewedBy: string | null;
  decidedBy: string | null;
  updatedAt: string;
}

export interface PartnerLoanDetail {
  application: PartnerLoanApplication;
  audit: AuditEvent[];
}

export interface PartnerOverview {
  byStatus: Record<LoanStatus, { count: number; amount: number }>;
  total: number;
  last30Days: number;
}

export interface LenderAccount extends User {
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

/** Trip terms an administrator sets for a customer. */
export interface TripProfile {
  destination: string | null;
  departureDate: string | null;
  finalPaymentDate: string | null;
  rateLockContribution: number | null;
  notes: string | null;
  updatedAt: string;
}

// --- Administration ---------------------------------------------------------------------------
export interface AdminUserSummary extends User {
  status: "pending" | "active" | "suspended" | "closed";
  createdAt: string;
  lastLoginAt: string | null;
  walletCount: number;
  totalAvailable: number;
  trip: Pick<TripProfile, "destination" | "departureDate" | "finalPaymentDate" | "rateLockContribution"> | null;
}

export interface AuditEvent {
  id: string;
  action: string;
  actorType: string;
  targetType?: string;
  targetId?: string | null;
  outcome: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminKuspitIdentity {
  idExterno: string;
  usuario: string | null;
  contrato: string | null;
  linkedAt: string;
  tokenExpiresAt: string | null;
  tokenRefreshedAt: string | null;
}

export interface AdminSpeiDestination {
  id: string;
  clabe: string;
  label: string | null;
  isDefault: boolean;
  kuspitStatus: KuspitDestinationStatus;
  action: string | null;
  contract: string | null;
  submittedAt: string | null;
  authorizedAt: string | null;
  message: string | null;
  documentName: string | null;
}

export interface AdminUserDetail {
  /** mfaEnrolledAt: when the person set up their authenticator (staff and partners), or null. */
  user: User & { status: string; createdAt: string; lastLoginAt: string | null; speiAccounts: number; mfaEnrolledAt?: string | null };
  /** The customer's CLABEs and each one's Kuspit registration state. */
  speiDestinations: AdminSpeiDestination[];
  wallets: Wallet[];
  /** The customer's Kuspit link; null until their first wallet linked them. */
  kuspit: AdminKuspitIdentity | null;
  trip: TripProfile | null;
  audit: AuditEvent[];
}

export interface AttentionItem {
  key: string;
  severity: "danger" | "warning" | "info";
  title: string;
  detail: string;
  to: string;
}

export interface AdminOverview {
  counts: {
    users: number; active_users: number; new_users_30d: number; wallets: number; active_wallets: number; pending_wallets: number;
    media: number; destinations: number; pending_kyc: number; open_loans: number; loans: number; lenders: number;
    open_requests: number; active_apartados: number; published_trips: number; investment_review: number;
  };
  balances: {
    total: number; pending_in: number; pending_out: number; interest: number;
    deposits_30d: number; withdrawals_30d: number; deposit_count_30d: number; withdrawal_count_30d: number;
  };
  kuspit: { linked: number; contracts: number; syncFailed: number; failedNotifications: number; staleWallets: number; stuckWallets: number; oldInstructions: number };
  /** The fund sub-ledger: unexplained differences between Kuspit and the pots, and contracts whose journal is not open yet. */
  fund: { openBreaks: number; unopenedLedgers: number };
  lending: Record<LoanStatus, { count: number; amount: number }>;
  attention: AttentionItem[];
  /** Fourteen calendar days of settled flows, oldest first. */
  series: Array<{ day: string; deposits: number; withdrawals: number }>;
  recentAudit: AuditEvent[];
}

// --- Fund sub-ledger (Fondos) -----------------------------------------------------------------
/**
 * How one contract's Kuspit balance is split, to the centavo. libre + apartados + pendingOut +
 * applied + suspense = total; `difference` is Kuspit minus total (zero when reconciled, null before
 * the first read); `spendable` is what the customer may move right now.
 */
export interface FundPots {
  opened: boolean;
  libre: number;
  apartados: number;
  pendingOut: number;
  applied: number;
  suspense: number;
  total: number;
  spendable: number;
  difference: number | null;
}

export type FundRunStatus = "opened" | "matched" | "pending_window" | "break";
export type FundBreakStatus = "open" | "resolved" | "cleared";
export type FundBreakTarget = "libre" | "suspense" | "trip_settlement";

export interface FundOverview {
  pots: Omit<FundPots, "opened" | "spendable" | "difference">;
  provider: { total: number; asOf: string | null };
  difference: number;
  wallets: { withContract: number; opened: number; unopened: number };
  breaks: { open: number; openAmount: number; oldestOpenAt: string | null; resolved30d: number; cleared30d: number };
  runs24h: Partial<Record<FundRunStatus, number>>;
  lastRunAt: string | null;
}

export interface FundWalletRow {
  id: string;
  product: "libre" | "travel";
  status: Wallet["status"];
  accountRef: string | null;
  owner: { id: string; fullName: string; email: string };
  provider: { available: number; asOf: string | null } | null;
  sync: { lastSyncedAt: string | null; lastError: string | null };
  pots: FundPots;
  lastRun: { status: FundRunStatus; at: string } | null;
  openBreak: { id: string; difference: number; firstSeenAt: string; occurrences: number } | null;
}

export interface FundJournalEntry {
  id: string;
  sequence: number;
  kind: string;
  amount: number;
  description: string;
  referenceType: string | null;
  referenceId: string | null;
  correlationId: string;
  actorType: string;
  actorId: string | null;
  metadata: Record<string, unknown>;
  entryHash: string;
  previousHash: string;
  occurredAt: string;
  createdAt: string;
  postings: Array<{ account: string; amount: number; balanceAfter: number }>;
}

export interface FundBreak {
  id: string;
  status: FundBreakStatus;
  difference: number;
  provider: number;
  ledger: number;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrences: number;
  resolutionEntryId: string | null;
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  clearedAt: string | null;
}

export interface FundWalletDetail {
  wallet: {
    id: string; product: "libre" | "travel"; status: Wallet["status"]; accountRef: string | null;
    owner: { id: string; fullName: string; email: string };
    provider: { available: number; accruedInterest: number; asOf: string } | null;
    sync: { lastSyncedAt: string | null; lastAttemptAt: string | null; lastError: string | null };
  };
  pots: FundPots & { byApartado: Array<{ apartadoId: string; tripName: string | null; balance: number }> };
  apartados: Array<{ id: string; tripName: string; status: string; balance: number }>;
  journal: FundJournalEntry[];
  runs: Array<{
    id: string; status: FundRunStatus; provider: number; ledger: number; pendingOut: number; difference: number; yieldPosted: number;
    providerAsOf: string | null; details: Record<string, unknown>; createdAt: string;
  }>;
  breaks: FundBreak[];
}

export interface FundVerification {
  ok: boolean;
  entries: number;
  postings: number;
  accounts: number;
  failure: { kind: string; entryId?: string; accountId?: string; detail: string } | null;
  checkedAt: string;
}

// --- Kuspit operations panel -----------------------------------------------------------------
export type KuspitNotificationStatus = "received" | "processed" | "ignored" | "failed";
/** Whether the worker's balance read from Kuspit is working for a wallet. */
export type BalanceSyncStatus = "synced" | "stale" | "failed" | "never";

export interface KuspitOverview {
  connector: { reachable: boolean; mode: "mock" | "live" | null; latencyMs: number | null };
  lastProbe: { at: string; ok: boolean; latencyMs: number } | null;
  identities: { linked: number; withContract: number; tokenValid: number; tokenExpiring: number; tokenExpired: number };
  wallets: { total: number; active: number; pending: number; suspended: number; closed: number; withContract: number; withClabe: number; pendingStuck: number; staleSync: number };
  /** Active wallets with a contract, by the state of their last Kuspit balance read. */
  sync: { fresh: number; stale: number; failed: number; never: number; lastSyncedAt: string | null; lastAttemptAt: string | null };
  notifications: {
    byStatus: Record<KuspitNotificationStatus, number>;
    byEvent: Array<{ code: string; count: number; failed: number }>;
    last24h: number;
    lastReceivedAt: string | null;
  };
  calls24h: {
    total: number; ok: number; errors: number; p50Ms: number | null; p95Ms: number | null;
    byEndpoint: Array<{ endpoint: string; total: number; ok: number; errors: number; p95Ms: number | null; lastAt: string }>;
  };
  /** What Kuspit holds across wallets (latest snapshot each) and what the platform has in flight. */
  custody: {
    providerTotal: number; lockedTotal: number; interestTotal: number; providerAsOf: string | null;
    pendingIn: number; pendingOut: number; pendingInstructions: number; pendingInstructionsOld: number;
  };
}

export interface KuspitContract {
  owner: { id: string; fullName: string; email: string; status: string };
  identity: { idExterno: string; usuario: string | null; contrato: string | null; linkedAt: string | null; tokenExpiresAt: string | null; tokenRefreshedAt: string | null } | null;
  wallet: {
    id: string; product: "libre" | "travel" | null; status: Wallet["status"] | null; accountRef: string | null; clabe: string | null;
    providerAttempts: number; createdAt: string | null;
    /** The balance Kuspit last reported; null until it has answered once. */
    balance: { available: number; locked: number; accruedInterest: number; asOf: string | null } | null;
    sync: { status: BalanceSyncStatus; lastSyncedAt: string | null; lastAttemptAt: string | null; lastError: string | null };
  } | null;
}

export interface KuspitNotification {
  id: string;
  notificationId: string;
  eventCode: string;
  folioConfirmacion: string;
  receivedAt: string;
  processedAt: string | null;
  status: KuspitNotificationStatus;
  attempts: number;
  lastError: string | null;
  walletId: string | null;
  ledgerEntryId: string | null;
  correlationId: string;
  payload: Record<string, unknown>;
  owner: { id: string; fullName: string | null; email: string | null } | null;
}

export interface KuspitCall {
  id: string;
  correlationId: string;
  endpoint: string;
  httpStatus: number | null;
  resultCode: string | null;
  latencyMs: number;
  createdAt: string;
}

export interface KuspitProbeResult {
  connector: KuspitOverview["connector"];
  probe: { mode: "mock" | "live"; checked: number; reachable: number; targets: Array<{ host: string; port: number; reachable: boolean; latencyMs: number | null; failure: string | null }> } | null;
  error: string | null;
}

// --- Peso a Peso panel -----------------------------------------------------------------------
export interface LendingReviewer extends LenderAccount {
  decisions: number;
  reviews: number;
  lastDecisionAt: string | null;
}

export interface LendingRecent {
  id: string;
  folio: string;
  status: LoanStatus;
  amount: number;
  termMonths: number;
  applicant: string;
  submittedAt: string;
  decidedAt: string | null;
  userId: string;
}

export interface LendingOverview {
  byStatus: Record<LoanStatus, { count: number; amount: number }>;
  total: number;
  applicants: number;
  last30Days: number;
  last7Days: number;
  averageAmount: number | null;
  approvalRate: number | null;
  averageDecisionHours: number | null;
  medianDecisionHours: number | null;
  byTerm: Array<{ termMonths: number; count: number; amount: number; approved: number }>;
  monthly: Array<{ month: string; submitted: number; submittedAmount: number; approved: number; approvedAmount: number; rejected: number }>;
  reviewers: LendingReviewer[];
  recent: LendingRecent[];
  oldestOpen: { id: string; folio: string; status: LoanStatus; amount: number; applicant: string; submittedAt: string; waitingHours: number } | null;
  settings: TnplSettings;
}

// --- Trips, reservation requests and Apartados (user stories v1.3) -------------------------------
export type Occupancy = "single" | "double" | "triple" | "quadruple" | "child";

export interface TripItineraryDay {
  day_number: number | null;
  title: string;
  cities: string[];
  description: string;
  meals: string[];
  activities: Array<{ name: string; type: string; description: string; included: boolean | null; duration_text: string; location: string; notes: string[] }>;
  transport: Array<{ type: string; origin: string; destination: string; duration_text: string; included: boolean | null; baggage_text: string }>;
  overnight: { city: string; hotel_ref: string; nights: number | null };
  notes: string[];
}

export interface TripSeason {
  valid_from: string | null;
  valid_to: string | null;
  /** When set, the departures this row prices (calendar-priced operators); the range is then display only. */
  departure_dates?: string[];
  service_category: string;
  rates: Array<{ occupancy: Occupancy; amount: number | null; currency: string }>;
}

/** Appendix A — the universal itinerary JSON (source and review blocks are admin-only). */
export interface TripContent {
  schema_version: string;
  source?: { file_name: string; source_language: string; extracted_at: string; source_refs: string[] };
  product: {
    trip_name: string; route_display: string; countries: string[]; cities: string[]; summary: string;
    duration_days: number | null; duration_nights: number | null; service_type: string; minimum_passengers: number | null;
  };
  availability: {
    arrival_days: string[];
    travel_periods: Array<{ start_date: string | null; end_date: string | null; source_text: string }>;
    departure_dates: string[];
    blackout_dates_text: string;
    valid_until: string | null;
  };
  itinerary: TripItineraryDay[];
  accommodations: Array<{ city: string; nights: number | null; hotel_options: string[]; category: string; meal_plan: string }>;
  pricing: {
    currency: string; price_scope: string; price_per: string; minimum_passengers: number | null;
    seasons: TripSeason[];
    add_ons: Array<{ name: string; description: string; amount: number | null; currency: string; unit: string; valid_from: string | null; valid_to: string | null; /** A date supplement: charged per person on these departures. */ departure_dates?: string[] }>;
    conditions: string[];
  };
  included: Array<{ category: string; description: string; quantity: number | null; conditions: string }>;
  excluded: Array<{ category: string; description: string }>;
  important_notes: Array<{ category: string; text: string; applies_to: string[] }>;
  web: {
    slug: string; seo_title: string; meta_description: string; hero_image: string | null; gallery: string[];
    cta: { label: string; url: string }; status: "draft" | "published" | "archived"; highlights: string[];
    /** Absent on content saved before 2026-10-08. */
    categories?: string[]; keywords?: string[];
  };
  review?: { missing_fields: string[]; ambiguities: string[]; conflicts: string[]; requires_human_review: boolean };
}

export interface TripCard {
  id: string;
  slug: string;
  status: "draft" | "published" | "archived";
  /** Catalogue placement: the region the card lists under and the destination whose card opens it. */
  region: { id: string; slug: string; name: string } | null;
  destination: { id: string; slug: string; name: string; country: string; category: DestinationCategory | string } | null;
  name: string;
  routeDisplay: string;
  countries: string[];
  cities: string[];
  summary: string;
  durationDays: number | null;
  durationNights: number | null;
  serviceType: string;
  minimumPassengers: number | null;
  priceFrom: { amount: number; currency: string; occupancy: Occupancy } | null;
  nextDeparture: string | null;
  departureCount: number;
  /** First day a departure may be requested (booking lead time applied). */
  earliestDeparture: string;
  arrivalDays: string[];
  validUntil: string | null;
  heroImageUrl: string | null;
  highlights: string[];
  /** Explora's category chips the trip appears under (playa, ciudad, aventura, cultura). */
  categories: DestinationCategory[];
  /** Extra words the search matches; never shown. */
  keywords: string[];
  publishedAt: string | null;
  updatedAt: string;
}

export interface TripPage extends TripCard {
  content: TripContent;
  version: number | null;
  preview: boolean;
  /** Photo gallery of the page: media URLs with the captions the tiles show. */
  gallery: TripGalleryPhoto[];
}

export interface TripGalleryPhoto { id: string | null; url: string; title: string | null }

// --- Trip pricing history (rate card per published version) -------------------------------------
export interface PricingSeasonSnapshot {
  key: string;
  label: string;
  validFrom: string | null;
  validTo: string | null;
  serviceCategory: string;
  rates: Partial<Record<Occupancy, number | null>>;
  /** Each occupancy relative to the double rate, in percent. */
  distribution: Partial<Record<Occupancy, number | null>>;
}

export interface PricingSnapshot {
  currency: string;
  priceScope: string;
  minimumPassengers: number | null;
  seasons: PricingSeasonSnapshot[];
  addOns: Array<{ name: string; amount: number | null; currency: string; unit: string }>;
  priceFrom: { amount: number; currency: string; occupancy: Occupancy } | null;
}

export interface PricingChange {
  type: "currency_changed" | "minimum_passengers_changed" | "price_from_changed" | "season_added" | "season_removed" | "season_dates_changed"
    | "rate_changed" | "rate_added" | "rate_removed" | "addon_added" | "addon_removed" | "addon_changed";
  text: string;
  season?: string;
  occupancy?: Occupancy;
  name?: string;
  from?: number | string | null | { validFrom: string | null; validTo: string | null };
  to?: number | string | null | { validFrom: string | null; validTo: string | null };
  delta?: number | null;
  pct?: number | null;
}

export interface PricingHistoryEntry {
  id: string;
  version: number;
  status: "current" | "superseded";
  publishedAt: string;
  publishedBy: string | null;
  author: string | null;
  note: string | null;
  changeCount: number;
  changes: PricingChange[];
  snapshot: PricingSnapshot;
}

export interface PricingStats {
  entries: number;
  changes: number;
  firstPublishedAt: string | null;
  lastChangeAt: string | null;
  currentSince: string | null;
  currency: string;
  priceFrom: { current: number | null; first: number | null; min: number | null; max: number | null; changePct: number | null } | null;
  distribution: Partial<Record<Occupancy, number | null>>;
  series: Array<{ season: string; occupancy: Occupancy; points: Array<{ version: number; publishedAt: string; amount: number | null }> }>;
}

export interface TripPricingHistory {
  history: PricingHistoryEntry[];
  stats: PricingStats;
  draft: { version: number; snapshot: PricingSnapshot; pendingChanges: PricingChange[] };
}

export interface TripPricingSummary { entries: number; changes: number; lastChangeAt: string | null; currentSince: string | null }

/** One entry of "Viajes guardados" (design turno 6). */
export interface SavedTrip {
  tripId: string;
  priceAlert: boolean;
  savedPrice: { amount: number; currency: string } | null;
  savedAt: string;
  trip: TripCard;
  /** Set when the current "desde" price differs from the one seen when saving. */
  priceChange: { previous: number; current: number; currency: string; direction: "down" | "up" } | null;
  apartado: { id: string; status: string; balance: number; goalAmount: number; progress: number | null } | null;
  request: { id: string; folio: string; status: RequestStatus } | null;
}

export interface AdminAnalytics {
  days: number;
  knownEvents: string[];
  summary: Array<{ name: string; count: number; users: number; lastSeen: string }>;
  daily: Array<{ day: string; count: number }>;
  recent: Array<{ id: string; name: string; source: string; properties: Record<string, unknown>; userId: string | null; occurredAt: string }>;
}

export interface AdminTrip extends Omit<TripCard, "status"> {
  status: "draft" | "published" | "archived";
  content?: TripContent;
  publishedContent?: TripContent | null;
  currentVersion: number;
  publishedVersion: number | null;
  heroPhotoId: string | null;
  regionId: string | null;
  destinationId: string | null;
  source: { fileName: string; uploadedAt: string | null; available: boolean } | null;
  validation: { errors: string[]; review: TripContent["review"] | null };
  publicationBlockers: string[];
  card: TripCard;
  reviewedAt: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  pricingHistory?: TripPricingSummary;
  /** Requests, Apartados and saved-trip entries pointing at the trip (what a deletion detaches). */
  usage?: TripUsage;
  /** Other trips that look like the same product (same name, same route and duration, same slug base). */
  duplicates?: DuplicateCandidate[];
}

export interface TripUsage { requests: number; apartados: number; saved: number }

export interface DuplicateCandidate { id: string; slug: string; name: string; status: "draft" | "published" | "archived"; reason: "name" | "route" | "slug" }

export interface AdminTripsIndex {
  trips: AdminTrip[];
  counts: { all: number; published: number; draft: number; archived: number; duplicates: number };
}

export interface AdminTripDetail {
  trip: AdminTrip & { content: TripContent; gallery: TripGalleryPhoto[]; duplicates: DuplicateCandidate[] };
  versions: Array<{ id: string; version: number; kind: string; note: string | null; author: string | null; createdAt: string }>;
  usage: TripUsage;
  pricingHistory: TripPricingSummary;
}

export interface ReservationConfig {
  apartadoMinPerPassengerMxn: number;
  usdToMxnRate: number;
  proposalValidityDays: number;
  minLeadDays: number;
  earliestDepartureDate: string;
  advisorResponseHours: number;
  maxTravelers: number;
  maxChildAge: number;
  consentVersion: string;
  documentsVersion: string;
  prospectusUrl: string;
  fundInfoUrl: string;
  occupancies: Array<{ id: Occupancy; short: string; long: string; capacity: number }>;
  /** The advisor card in Mis solicitudes: name and the channels "Escribir" can open. */
  advisor: { name: string; email: string; whatsapp: string };
  investment: { enabled: boolean; fundLabel: string; infoText: string };
}

export type RequestStatus = "submitted" | "in_review" | "proposal_ready" | "services_confirmed" | "confirmed" | "expired" | "cancelled" | "unavailable";
export type RequestStatusGroup = "review" | "proposal" | "confirmed" | "closed";
export type RequestKind = "reservation" | "personalized";

export interface RequestTimelineEvent {
  id: string;
  fromStatus: RequestStatus | null;
  toStatus: RequestStatus;
  actorType: "customer" | "admin" | "service";
  note: string | null;
  createdAt: string;
}

export interface ReservationRequest {
  id: string;
  folio: string;
  kind: RequestKind;
  status: RequestStatus;
  statusGroup: RequestStatusGroup;
  trip: { id: string | null; slug: string | null; name: string; summary: string | null; photoUrl: string | null };
  departureDate: string | null;
  travelers: number;
  adults: number;
  children: number;
  childAges: number[];
  rooms: Occupancy[];
  occupancyLabel: string | null;
  passportConfirmed: boolean;
  contact: { fullName: string | null; email: string | null; phone: string | null };
  pricing: {
    pricePerPerson: number | null; currency: string; estimatedTotal: number | null; estimatedTotalMxn: number | null;
    quotedTotal: number | null; quotedCurrency: string | null; proposalValidUntil: string | null;
  };
  details: Record<string, unknown>;
  advisorNote: string | null;
  duplicatedFrom: string | null;
  submittedAt: string;
  lastActivityAt: string;
  updatedAt: string;
  apartado: { id: string; status: string; balance: number; goalAmount: number } | null;
  timeline: RequestTimelineEvent[];
}

export interface AdminReservationRequest extends ReservationRequest {
  customer: { id: string; fullName: string; email: string; phone?: string | null };
}

export interface ApartadoEligibility {
  eligible: boolean;
  code: "ok" | "existing" | "closed" | "personalized" | "expired" | "no_context" | "wallet_required";
  message: string;
  minimumContribution: number;
  goalAmount: number | null;
  existingApartadoId: string | null;
  travelers: number;
}

export type ApartadoStatus = "funding_pending" | "active" | "funding_failed" | "completed" | "closed";
export type MovementKind = "contribution" | "goal_change" | "disposal" | "reversal" | "adjustment";

export interface ApartadoMovement {
  id: string;
  kind: MovementKind;
  amount: number;
  balanceAfter: number;
  previousGoal: number | null;
  newGoal: number | null;
  actorType: "customer" | "admin" | "service";
  reason: string | null;
  destination: string | null;
  reference: string | null;
  status: "completed" | "failed" | "reversed" | "pending";
  reversesMovementId: string | null;
  createdAt: string;
  approvalReference?: string | null;
  correlationId?: string;
}

export interface Apartado {
  id: string;
  walletId: string;
  requestId: string;
  tripId: string | null;
  tripName: string;
  status: ApartadoStatus;
  goalAmount: number;
  baseAmount: number;
  balance: number;
  appliedAmount: number;
  progress: number | null;
  remaining: number;
  minimumGoal: number;
  consentVersion: string;
  documentsVersion: string | null;
  request: { id: string; folio: string; status: RequestStatus; tripSlug: string | null; departureDate: string | null; travelers: number; tripPhotoUrl: string | null } | null;
  movements: ApartadoMovement[];
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  /** Wallet Libre money still free to move into this Apartado (customer detail only). */
  walletFree?: number | null;
}

export interface AdminApartado extends Apartado {
  customer: { id: string; fullName: string; email: string };
  folio: string;
  requestStatus: RequestStatus;
}

export interface AdminApartadoDetail {
  apartado: AdminApartado;
  wallet: { id: string; available: number | null; reserved: number; asOf: string | null };
  audit: AuditEvent[];
}

export interface ReservationSettings {
  apartadoMinPerPassengerMxn: number;
  usdToMxnRate: number;
  proposalValidityDays: number;
  minLeadDays: number;
  advisorResponseHours: number;
  maxTravelers: number;
  maxChildAge: number;
  advisorEmail: string;
  advisorName: string;
  advisorWhatsapp: string;
  consentVersion: string;
  documentsVersion: string;
  prospectusUrl: string;
  fundInfoUrl: string;
}

export interface InvestmentSettings {
  enabled: boolean;
  fundIssuer: string;
  fundTicker: string;
  fundSeries: string;
  fundLabel: string;
  requireKuspitVerificationForWithdrawal: boolean;
  enforceUnverifiedDepositLimit: boolean;
  unverifiedDepositLimitUdi: number;
  udiValueMxn: number;
  infoText: string;
}

export interface InvestmentOrder {
  id: string;
  side: "buy" | "sell";
  amount: number;
  fund: { issuer: string; ticker: string; series: string; label: string };
  status: "pending" | "submitting" | "submitted" | "assigned" | "cancelled" | "failed" | "manual_review";
  titles: number | null;
  price: number | null;
  executedAmount: number | null;
  providerOrderRef: string | null;
  ledgerEntryId: string | null;
  failureCode: string | null;
  attempts: number;
  submittedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

// --- Shapes that used to live next to the screen that read them --------------------------------

/** GET /wallets/:id/funding-instructions — where a SPEI deposit into the wallet goes. */
export interface FundingInstructions {
  clabe: string;
  beneficiaryName?: string | null;
  currency: string;
  source: string;
  /** Kuspit's monthly cap while the file is incomplete; a SPEI over it is bounced, so it is shown beside the CLABE. */
  depositLimit?: DepositLimit | null;
  /** True only against the Kuspit mock, where an instructed top-up settles; live funding is an SPEI to the CLABE. */
  instructedDeposits?: boolean;
}

/** GET /reservations → `{ requests, counts }`: how many of the customer's requests sit in each status group. */
export interface RequestCounts { all: number; review: number; proposal: number; confirmed: number; closed: number }

// --- Know your customer (Kuspit) ---------------------------------------------------------------

export interface KycProfile {
  firstName: string;
  paternalSurname: string;
  maternalSurname: string | null;
  mobilePhone: string;
  birthDate: string;
  sex: "H" | "M";
  curp: string;
  rfc: string | null;
  birthCountryId: string;
  birthStateId: string | null;
  postalCode: string;
  stateId: string;
  stateName: string | null;
  municipalityId: string;
  municipalityName: string | null;
  colonyId: string;
  colonyName: string | null;
  street: string;
  exteriorNumber: string;
  interiorNumber: string | null;
  updatedAt: string;
}

export type KycFileDocumentKind = "id_front" | "id_back" | "curp" | "rfc" | "proof_of_address";
export type KycFileStatus = "not_started" | "in_progress" | "submitted" | "approved" | "rejected";

export interface KycFile {
  status: KycFileStatus;
  submittedAt: string | null;
  note: string | null;
  identification: "ine" | "passport" | null;
  documents: Array<{ kind: KycFileDocumentKind; required: boolean; status: "missing" | "submitted" | "validated" | "rejected"; fileName: string | null; reason: string | null; uploadedAt: string | null }>;
  transactionalProfile: boolean;
  missing: string[];
}

export interface KycState {
  profile: KycProfile | null;
  profileRequired: boolean;
  profileLocked: boolean;
  contract: string | null;
  file: KycFile;
}

export interface CatalogueOption { id: string; label: string }
export interface PostalCodeLookup {
  postalCode: string;
  country: CatalogueOption | null;
  state: CatalogueOption | null;
  municipalities: Array<CatalogueOption & { colonies: CatalogueOption[] }>;
}
